import { prefersReducedMotion, onDocumentReady } from '@theme/utilities';

/**
 * CSS class name for active nav link
 */
const ACTIVE_CLASS = 'active';

/**
 * CSS class name for visible nav (faded in)
 */
const VISIBLE_CLASS = 'page-nav-visible';

/**
 * Data attribute to identify page nav elements
 */
const PAGE_NAV_ATTR = 'data-page-nav';

/**
 * Attribute to track if element has been initialized
 */
const INITIALIZED_ATTR = 'data-page-nav-initialized';

/**
 * Root margin for IntersectionObserver
 * -20% top means section is considered "active" when its top edge passes 20% from top of viewport
 */
const ROOT_MARGIN = '-20% 0px -80% 0px';

/**
 * Initializes scroll spy functionality for page navigation
 */
function initPageNav() {
  // Find all page nav elements
  const pageNavs = document.querySelectorAll(`[${PAGE_NAV_ATTR}]`);

  if (pageNavs.length === 0) {
    return;
  }

  const shouldReduceMotion = prefersReducedMotion();

  pageNavs.forEach((nav) => {
    if (nav.hasAttribute(INITIALIZED_ATTR)) {
      return; // Already initialized
    }

    nav.setAttribute(INITIALIZED_ATTR, '');
    
    // If user prefers reduced motion, show nav immediately
    if (shouldReduceMotion) {
      nav.classList.add(VISIBLE_CLASS);
    }

    // Get all nav links
    const navLinks = nav.querySelectorAll('a[href^="#"]');
    if (navLinks.length === 0) {
      return;
    }

    // Extract section IDs from href attributes
    const sectionIds = Array.from(navLinks)
      .map((link) => {
        const href = link.getAttribute('href');
        return href ? href.substring(1) : null;
      })
      .filter(/** @param {string | null} id */ (id) => Boolean(id));

    if (sectionIds.length === 0) {
      return;
    }

    // Find all target sections
    const sections = sectionIds
      .map((id) => (id ? document.getElementById(id) : null))
      .filter(/** @param {HTMLElement | null} section */ (section) => Boolean(section));

    if (sections.length === 0) {
      return;
    }

    // Create a map of section ID to nav link
    const sectionToLinkMap = new Map();
    navLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        const id = href.substring(1);
        sectionToLinkMap.set(id, link);
      }
    });

    // Track currently active section
    /** @type {string | null} */
    let activeSectionId = null;
    
    // Track if nav has been faded in
    let navVisible = shouldReduceMotion; // If reduced motion, nav is already visible
    
    // Get the first section to watch for fade-in
    const firstSection = sections[0];

    /**
     * Fades in the nav when the first section enters the viewport
     */
    function checkFirstSectionVisibility() {
      if (navVisible || !firstSection || shouldReduceMotion) {
        return;
      }

      const rect = firstSection.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        nav.classList.add(VISIBLE_CLASS);
        navVisible = true;
      }
    }

    /**
     * Updates the active nav link based on which section is in view
     */
    function updateActiveLink() {
      const viewportHeight = window.innerHeight;
      const threshold = viewportHeight * 0.2;

      // If we're at the bottom of the page, activate the last section
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10) {
        const lastSection = sections[sections.length - 1];
        if (lastSection) {
          const lastSectionId = lastSection.id;
          if (lastSectionId !== activeSectionId) {
            // Remove active class from all links
            navLinks.forEach((link) => link.classList.remove(ACTIVE_CLASS));
            // Add active class to last section's link
            const lastLink = sectionToLinkMap.get(lastSectionId);
            if (lastLink) {
              lastLink.classList.add(ACTIVE_CLASS);
              activeSectionId = lastSectionId;
            }
          }
          return;
        }
      }

      // Find sections that have passed the 20% threshold (top edge is at or above threshold)
      /** @type {Array<{ section: HTMLElement; top: number; id: string }>} */
      const passedThreshold = sections
        .map((section) => {
          if (!section) return null;
          const rect = section.getBoundingClientRect();
          return {
            section,
            top: rect.top,
            id: section.id,
          };
        })
        .filter(/** @param {{ section: HTMLElement; top: number; id: string } | null} item */ (item) => item !== null && item.top <= threshold)
        .map(/** @param {{ section: HTMLElement; top: number; id: string } | null} item */ (item) => /** @type {{ section: HTMLElement; top: number; id: string }} */ (item))
        .sort((a, b) => b.top - a.top); // Sort by top position descending (closest to threshold first)

      // If any sections have passed the threshold, use the one closest to it (most recently passed)
      /** @type {HTMLElement | null} */
      let newActiveSection = null;
      if (passedThreshold.length > 0 && passedThreshold[0]) {
        newActiveSection = passedThreshold[0].section;
      } else if (sections.length > 0 && sections[0]) {
        // If no sections have passed yet, use the first section
        newActiveSection = sections[0];
      }

      const newActiveSectionId = newActiveSection ? newActiveSection.id : null;

      // Only update if the active section has changed
      if (newActiveSectionId !== activeSectionId) {
        // Remove active class from all links
        navLinks.forEach((link) => link.classList.remove(ACTIVE_CLASS));

        // Add active class to the new active link
        if (newActiveSectionId) {
          const activeLink = sectionToLinkMap.get(newActiveSectionId);
          if (activeLink) {
            activeLink.classList.add(ACTIVE_CLASS);
          }
        }

        activeSectionId = newActiveSectionId;
      }
    }

    // Create IntersectionObserver to watch sections
    const observer = new IntersectionObserver(
      () => {
        updateActiveLink();
      },
      {
        rootMargin: ROOT_MARGIN,
        threshold: [0, 0.1, 0.5, 1],
      }
    );

    // Observe all sections
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    // Set initial active state
    updateActiveLink();
    
    // Check if first section is already visible on load
    if (!shouldReduceMotion) {
      checkFirstSectionVisibility();
      
      // Watch first section for fade-in using IntersectionObserver
      if (firstSection) {
        const fadeInObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !navVisible) {
                nav.classList.add(VISIBLE_CLASS);
                navVisible = true;
                fadeInObserver.disconnect();
              }
            });
          },
          {
            threshold: 0.1, // Trigger when 10% of section is visible
          }
        );
        
        fadeInObserver.observe(firstSection);
      }
    }

    // Also update on scroll for more responsive updates
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        updateActiveLink();
        // Also check first section visibility on scroll
        if (!navVisible) {
          checkFirstSectionVisibility();
        }
      }, 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Handle dynamically added content
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldReinit = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
            // Check if any new sections with our target IDs were added
            sectionIds.forEach((id) => {
              if (node.id === id || node.querySelector(`#${id}`)) {
                shouldReinit = true;
              }
            });
          }
        });
      });

      if (shouldReinit) {
        // Reinitialize if new sections were added
        observer.disconnect();
        const newSections = sectionIds
          .map((id) => (id ? document.getElementById(id) : null))
          .filter(/** @param {HTMLElement | null} section */ (section) => Boolean(section));
        newSections.forEach((section) => {
          if (section) {
            observer.observe(section);
          }
        });
        updateActiveLink();
      }
    });

    // Observe the main content area for new sections
    const mainContent = document.querySelector('#MainContent');
    if (mainContent) {
      mutationObserver.observe(mainContent, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id'],
      });
    }
  });
}

// Initialize when DOM is ready
onDocumentReady(initPageNav);

